import { formatMoney } from '@forecastify/shared';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C58 · the dashboard's number rules, in one place.                         │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **EVERY PANEL ON THIS SCREEN FORMATS THE SAME FIGURES, AND THEY MUST LOOK THE SAME.** A
 * KPI tile saying *+352 615 Ft* over a table cell saying *352615.00* is the same number twice,
 * and a reader checking one against the other cannot tell. The mockup's own helpers — `fK`, `fKs`,
 * `fKc`, `pc`, `ix` — are transcribed here rather than reinvented per component.
 *
 * ⚠️ **The MINUS is U+2212, not a hyphen**, throughout. A hyphen in a column of `tabular-nums`
 * sits at a different height and a different width from the digits beside it, and a negative
 * margin is the one figure on this screen that must read as negative at a glance.
 */

export interface Currency {
  readonly code: string;
  readonly symbol: string;
}

/** `10 198 000 Ft`. A dash where there is no figure — never a confident zero. */
export function money(value: number | null, currency: Currency): string {
  if (value === null || Number.isNaN(value)) return '–';
  return formatMoney(value, currency);
}

/**
 * `+352 615 Ft` / `−374 118 Ft` — a figure whose SIGN is the point.
 *
 * ⚠️ **A near-zero value gets no sign at all.** `+0 Ft` on a margin reads as a deliberate
 * break-even; a bare `0 Ft` reads as nothing to report, which is what it is.
 */
export function signedMoney(value: number | null, currency: Currency): string {
  if (value === null || Number.isNaN(value)) return '–';
  const sign = value > 0.4 ? '+' : value < -0.4 ? '−' : '';
  return `${sign}${formatMoney(Math.abs(value), currency)}`;
}

/**
 * `6,4 M Ft` — the compact form the chart's axis needs.
 *
 * ⚠️ **Thousands are `e` in Hungarian and `k` where the symbol leads.** The mockup switches on the
 * currency, because *6,4 e Ft* is right and *€ 6,4 e* is not.
 */
export function compactMoney(value: number | null, currency: Currency): string {
  if (value === null || Number.isNaN(value)) return '–';
  const symbolFirst = ['EUR', 'USD', 'GBP'].includes(currency.code);
  const sign = value < 0 ? '−' : '';
  const abs = Math.abs(value);

  let scaled: number | null = null;
  let unit = '';
  if (abs >= 1e9) {
    scaled = abs / 1e9;
    unit = ' Mrd';
  } else if (abs >= 1e6) {
    scaled = abs / 1e6;
    unit = ' M';
  } else if (abs >= 1e3) {
    scaled = abs / 1e3;
    unit = symbolFirst ? ' k' : ' e';
  }
  if (scaled === null) return sign + formatMoney(abs, currency);

  const digits = (Math.round(scaled * 10) / 10).toFixed(1).replace('.', ',');
  return symbolFirst
    ? `${sign}${currency.symbol} ${digits}${unit}`
    : `${sign}${digits}${unit} ${currency.symbol}`;
}

/** `41%`, or a dash. ⚠️ Takes a RATIO, so a caller cannot forget to divide. */
export function percent(ratio: number | null): string {
  if (ratio === null || Number.isNaN(ratio) || !Number.isFinite(ratio)) return '–';
  return `${String(Math.round(ratio * 100)).replace('-', '−')}%`;
}

/**
 * `0,94` — an index, always two places.
 *
 * ⚠️ **Two places and never rounded to one.** CPI 0,94 and CPI 0,9 are different statements: the
 * first says *6% over*, the second says *somewhere between 5% and 15% over*, and this screen turns
 * red at 0,92.
 */
export function index(value: number | null): string {
  if (value === null || Number.isNaN(value) || !Number.isFinite(value)) return '–';
  return value.toFixed(2).replace('.', ',').replace('-', '−');
}

/** `592:43` — hours, in the product's one hour format. */
export function hours(value: number): string {
  const whole = Math.floor(Math.abs(value));
  const minutes = Math.round((Math.abs(value) - whole) * 60);
  const carried = minutes === 60 ? whole + 1 : whole;
  const shown = minutes === 60 ? 0 : minutes;
  return `${value < 0 ? '−' : ''}${String(carried)}:${String(shown).padStart(2, '0')}`;
}

/** A share of a whole, clamped for a bar's width. ⚠️ The COLOUR says it went over, not the width. */
export function barWidth(value: number, scale: number): number {
  if (scale <= 0) return 0;
  return Math.max(0, Math.min(100, (value / scale) * 100));
}
